import React, { useState, useEffect, useMemo, useCallback } from "react";
import { PlaygroundType, type ExposureType } from "@/services/api/widget/types";
import FormInput from "./ui/form-input";
import SelectDropdown, { type SelectOption } from "./ui/select-dropdown";
import ButtonGroup, { type ButtonGroupOption } from "./ui/button-group";
import { getAvailableExposures } from "@/services/api/widget";
import type { ExtendedAssetInfo } from "@/components/playground/hooks/useAssetManager";
import type { StatsView, StatsViewColumn } from "@/services/api/statistics/types";
import { StatisticsSelector } from "@/components/playground/components/StatisticsSelector";
import { ExposureBandsDialog } from "@/components/playground/components/ExposureBandsDialog";
import type { ExposureBandsMap } from "@/components/playground/adapters/echarts/types";
import { Switch } from "@/components/ui/switch/Switch";
import * as StatisticsAPI from "@/services/api/statistics";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faTrash, faStar } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { faStar as faStarSolid } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { useAuth } from "@/context/AuthContext";
import { isPlaygroundTypeAvailable } from "@/components/playground/playgroundAvailability";

function ExtendIncompleteWithProxySwitch({
  checked,
  onChange,
  description,
  className = "pt-2",
}: {
  checked: boolean;
  onChange: (enabled: boolean) => void;
  description: string;
  className?: string;
}) {
  return (
    <div className={`flex items-start justify-between gap-3 ${className}`}>
      <div>
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Extend incomplete performance with proxy
        </label>
        <p className="text-xs text-slate-400 mt-1">{description}</p>
      </div>
      <Switch
        checked={checked}
        onChange={onChange}
        data-testid="extend-incomplete-with-proxy-switch"
      >
        <span className="sr-only">Extend incomplete performance with proxy</span>
      </Switch>
    </div>
  );
}

// Available statistics for Rolling Stats
export type StatisticType =
  | "volatility"
  | "returns"
  | "sharpe_ratio"
  | "sortino_ratio"
  | "max_drawdown"
  | "beta"
  | "correlation"
  | "underwater";

export const METRIC_FIELD_PATH_OPTIONS: SelectOption<string>[] = [
  {
    value: "calculated_fields.statistics.saltus-stat-volatility-3y-m-ex-ante.value",
    label: "3Y Ex-Ante Volatility",
  },
  {
    value:
      "calculated_fields.statistics.saltus-stat-rel-volatility-3y-m-ex-ante.value",
    label: "3Y Ex-Ante Relative Volatility",
  },
];

export interface WidgetConfigProps {
  frequency: "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
  setFrequency: (
    frequency: "daily" | "weekly" | "monthly" | "quarterly" | "yearly"
  ) => void;
  displayMode: "index" | "performance";
  setDisplayMode: (mode: "index" | "performance") => void;
  rebaseValue: number;
  setRebaseValue: (value: number) => void;
  playgroundType: PlaygroundType;
  setPlaygroundType?: (type: PlaygroundType) => void;

  // Rolling Stats specific props
  statistic?: StatisticType;
  setStatistic?: (statistic: StatisticType) => void;
  windowSize?: number;
  setWindowSize?: (size: number) => void;
  metricFieldPath?: string;
  setMetricFieldPath?: (path: string) => void;

  // Exposure specific props
  exposureType?: ExposureType;
  setExposureType?: (type: ExposureType) => void;
  exposureDisplayMode?: "weights" | "scaled_weights" | "values";
  setExposureDisplayMode?: (
    mode: "weights" | "scaled_weights" | "values"
  ) => void;
  exposureCategories?: string[];
  exposureBands?: ExposureBandsMap | null;
  onSaveExposureBands?: (
    bands: ExposureBandsMap
  ) => { success: boolean; error?: string };
  onClearExposureBands?: () => void;

  // Asset selection props for dynamic exposure types
  selectedAssets?: string[];
  assetMetadata?: Record<string, ExtendedAssetInfo>;
  defaultResourceType?: "product" | "portfolio" | "benchmark" | "index";

  // Scatter Chart specific props
  xStatistic?: string;
  setXStatistic?: (statistic: string) => void;
  yStatistic?: string;
  setYStatistic?: (statistic: string) => void;

  // Statistics Table specific props - unified columns array
  columns?: StatsViewColumn[];
  setColumns?: (columns: StatsViewColumn[]) => void;
  showBenchmarks?: boolean;
  setShowBenchmarks?: (show: boolean) => void;
  extendIncompleteWithProxy?: boolean;
  setExtendIncompleteWithProxy?: (enabled: boolean) => void;
  /** Annual risk-free rate for Sharpe/Sortino (decimal, e.g., 0.02 for 2%) */
  riskFreeRate?: number;
  setRiskFreeRate?: (rate: number) => void;
}

const WidgetConfig: React.FC<WidgetConfigProps> = ({
  frequency,
  setFrequency,
  displayMode,
  setDisplayMode,
  rebaseValue,
  setRebaseValue,
  playgroundType,
  setPlaygroundType = () => {},
  statistic = "volatility",
  setStatistic = () => {},
  windowSize = 30,
  setWindowSize = () => {},
  metricFieldPath = METRIC_FIELD_PATH_OPTIONS[0].value,
  setMetricFieldPath = () => {},
  exposureType = "asset_allocation",
  setExposureType = () => {},
  exposureDisplayMode = "scaled_weights",
  setExposureDisplayMode = () => {},
  exposureCategories = [],
  exposureBands = null,
  onSaveExposureBands,
  onClearExposureBands,
  selectedAssets = [],
  assetMetadata = {},
  defaultResourceType = "product",
  xStatistic = "generic-stat-annualized-3y",
  setXStatistic = () => {},
  yStatistic = "generic-stat-volatility-36m-m",
  setYStatistic = () => {},
  columns = [],
  setColumns = () => {},
  showBenchmarks = false,
  setShowBenchmarks = () => {},
  extendIncompleteWithProxy = false,
  setExtendIncompleteWithProxy = () => {},
  riskFreeRate = 0.02,
  setRiskFreeRate = () => {},
}) => {
  const { user } = useAuth();
  const [isBandsDialogOpen, setIsBandsDialogOpen] = useState(false);

  // State for dynamic exposure types
  const [availableExposureTypes, setAvailableExposureTypes] = useState<
    string[]
  >([]);
  const [isLoadingExposureTypes, setIsLoadingExposureTypes] = useState(false);
  const [exposureTypesError, setExposureTypesError] = useState<string | null>(
    null
  );

  // Fetch available exposure types when exposure chart is selected and assets are available
  useEffect(() => {
    if (
      (playgroundType !== PlaygroundType.Exposure &&
        playgroundType !== PlaygroundType.ExposureColumns &&
        playgroundType !== PlaygroundType.ExposureOverTime) ||
      selectedAssets.length === 0
    ) {
      if (availableExposureTypes.length !== 0) {
        setAvailableExposureTypes([]);
      }
      return;
    }

    const fetchAvailableExposures = async () => {
      setIsLoadingExposureTypes(true);
      setExposureTypesError(null);

      try {
        const firstAssetId = selectedAssets[0];
        const metadata = assetMetadata[firstAssetId];

        let resourceType = metadata?.resourceType || "product";
        let resourceId = metadata?.resourceId || firstAssetId;

        if (firstAssetId.includes(":")) {
          const [type, id] = firstAssetId.split(":", 2);
          if (type && id) {
            resourceType = type;
            resourceId = id;
          }
        }

        const response = await getAvailableExposures({
          resource_type: resourceType,
          resource_id: resourceId,
        });

        if (
          JSON.stringify(response.exposure_types) !==
          JSON.stringify(availableExposureTypes)
        ) {
          setAvailableExposureTypes(response.exposure_types);
        }

        // Set default exposure type
        if (response.exposure_types.length > 0) {
          if (!response.exposure_types.includes(exposureType)) {
            // If current exposure type is not available, prefer "asset_allocation" if available, otherwise use first
            const defaultType = response.exposure_types.includes(
              "asset_allocation"
            )
              ? "asset_allocation"
              : response.exposure_types[0];
            setExposureType(defaultType as ExposureType);
          } else if (!exposureType || exposureType === "") {
            // If no exposure type is set, prefer "asset_allocation" if available
            const defaultType = response.exposure_types.includes(
              "asset_allocation"
            )
              ? "asset_allocation"
              : response.exposure_types[0];
            setExposureType(defaultType as ExposureType);
          }
        }
      } catch {
        setExposureTypesError("Failed to load exposure types");
        setAvailableExposureTypes([
          "asset_allocation",
          "sector",
          "geography",
          "currency",
        ]);
      } finally {
        setIsLoadingExposureTypes(false);
      }
    };

    fetchAvailableExposures();
  }, [
    playgroundType,
    selectedAssets,
    assetMetadata,
    availableExposureTypes,
    exposureType,
  ]);

  // State for statistics selector dialog
  const [isStatisticsSelectorOpen, setIsStatisticsSelectorOpen] = useState(false);
  
  // Views state
  const [savedViews, setSavedViews] = useState<StatsView[]>([]);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");
  const [saveViewDescription, setSaveViewDescription] = useState("");
  const [savingView, setSavingView] = useState(false);

  // Track if we've done initial view load (to avoid re-selecting default when user picks Custom)
  const [hasLoadedInitialView, setHasLoadedInitialView] = useState(false);

  // Load saved views on mount
  const loadSavedViews = useCallback(async () => {
    setViewsLoading(true);
    try {
      const response = await StatisticsAPI.listStatsViews();
      console.log("[DEBUG] listStatsViews response:", response);
      console.log("[DEBUG] First view columns:", response.views?.[0]?.columns);
      setSavedViews(response.views || []);
      
      // Auto-select default view only on initial load
      if (!hasLoadedInitialView) {
        const defaultView = response.views?.find((v: StatsView) => v.is_default);
        console.log("[DEBUG] Default view found:", defaultView);
        if (defaultView && defaultView.id) {
          handleLoadView(defaultView);
        }
        setHasLoadedInitialView(true);
      }
    } catch (err) {
      console.error("Error loading saved views:", err);
    } finally {
      setViewsLoading(false);
    }
  }, [hasLoadedInitialView]);

  useEffect(() => {
    if (playgroundType === PlaygroundType.StatisticsTable) {
      loadSavedViews();
    }
  }, [playgroundType, loadSavedViews]);

  // Handle loading a saved view
  const handleLoadView = (view: StatsView) => {
    console.log("[DEBUG] handleLoadView called with:", view);
    console.log("[DEBUG] view.columns:", view.columns);
    
    setSelectedViewId(view.id || null);
    
    // Set columns directly - no conversion needed
    if (view.columns && view.columns.length > 0) {
      setColumns(view.columns);
    } else {
      console.log("[DEBUG] No columns found in view!");
    }
    
    if (view.show_benchmarks !== undefined) {
      setShowBenchmarks(view.show_benchmarks);
    }
  };

  // Handle switching to custom mode
  const handleSelectCustom = () => {
    setSelectedViewId(null);
  };

  // Handle saving current configuration as a new view
  const handleSaveView = async () => {
    if (!saveViewName.trim()) return;
    setSavingView(true);
    
    try {
      // Pass columns directly to API - no conversion needed
      const newView = await StatisticsAPI.createStatsView({
        name: saveViewName.trim(),
        description: saveViewDescription.trim() || undefined,
        columns,
        show_benchmarks: showBenchmarks,
      });
      
      setSavedViews((prev) => [...prev, newView]);
      setSelectedViewId(newView.id || null);
      setShowSaveDialog(false);
      setSaveViewName("");
      setSaveViewDescription("");
    } catch (err) {
      console.error("Error saving view:", err);
    } finally {
      setSavingView(false);
    }
  };

  // Handle deleting a view
  const handleDeleteView = async (view: StatsView, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!view.id) return;
    
    try {
      await StatisticsAPI.deleteStatsView(view.id);
      setSavedViews((prev) => prev.filter((v) => v.id !== view.id));
      if (selectedViewId === view.id) {
        setSelectedViewId(null);
      }
    } catch (err) {
      console.error("Error deleting view:", err);
    }
  };

  // Handle setting a view as default
  const handleSetDefaultView = async (view: StatsView, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!view.id) return;
    
    try {
      // First, unset current default
      const currentDefault = savedViews.find((v) => v.is_default);
      if (currentDefault?.id && currentDefault.id !== view.id) {
        await StatisticsAPI.updateStatsView(currentDefault.id, { is_default: false });
      }
      
      // Set new default
      await StatisticsAPI.updateStatsView(view.id, { is_default: true });
      
      // Update local state
      setSavedViews((prev) =>
        prev.map((v) => ({
          ...v,
          is_default: v.id === view.id,
        }))
      );
    } catch (err) {
      console.error("Error setting default view:", err);
    }
  };

  // Build view options for dropdown
  const viewOptions: SelectOption<string | null>[] = useMemo(() => {
    const options: SelectOption<string | null>[] = [
      { value: null, label: "Custom" },
    ];
    
    savedViews.forEach((view) => {
      options.push({
        value: view.id || null,
        label: view.is_default ? `${view.name} ★` : view.name,
      });
    });
    
    return options;
  }, [savedViews]);

  // Define options for dropdowns
  const chartTypeOptions: SelectOption<PlaygroundType>[] = useMemo(
    () =>
      [
        { value: PlaygroundType.PerformanceHistory, label: "Performance History" },
        { value: PlaygroundType.StatisticsTable, label: "Statistics Table" },
        { value: PlaygroundType.RollingStats, label: "Rolling Stats" },
        { value: PlaygroundType.MetricsOverTime, label: "Metrics Over Time" },
        { value: PlaygroundType.Underwater, label: "Underwater (Drawdown)" },
        { value: PlaygroundType.ScatterChart, label: "Scatter Chart" },
        { value: PlaygroundType.Exposure, label: "Exposure" },
        { value: PlaygroundType.ExposureColumns, label: "Exposure (Columns)" },
        { value: PlaygroundType.ExposureOverTime, label: "Exposure (Over time)" },
        { value: PlaygroundType.CorrelationMatrix, label: "Correlation Matrix" },
      ].filter((option) =>
        isPlaygroundTypeAvailable(user?.company_id, option.value),
      ),
    [user?.company_id],
  );

  const displayModeOptions: ButtonGroupOption<"index" | "performance">[] = [
    { value: "index", label: "Index" },
    { value: "performance", label: "Performance" },
  ];

  const frequencyOptions: SelectOption<
    "daily" | "weekly" | "monthly" | "quarterly" | "yearly"
  >[] = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
  ];

  const exposureOverTimeFrequencyOptions: SelectOption<
    "monthly" | "quarterly"
  >[] = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
  ];

  const exposureDisplayModeOptions: SelectOption<
    "weights" | "scaled_weights" | "values"
  >[] = [
    { value: "weights", label: "Weights" },
    { value: "scaled_weights", label: "Scaled weights" },
    { value: "values", label: "Values" },
  ];

  const selectedResourceType = useMemo(() => {
    if (selectedAssets.length !== 1) return null;
    const metadata = assetMetadata[selectedAssets[0]];
    if (metadata?.resourceType) return metadata.resourceType;
    if (selectedAssets[0].includes(":")) {
      return selectedAssets[0].split(":")[0];
    }
    return defaultResourceType;
  }, [selectedAssets, assetMetadata, defaultResourceType]);

  const exposureDisplayModeOptionsFiltered = useMemo(
    () =>
      selectedResourceType === "portfolio"
        ? exposureDisplayModeOptions
        : exposureDisplayModeOptions.filter((option) => option.value !== "values"),
    [selectedResourceType]
  );

  const statisticOptions: SelectOption<StatisticType>[] = [
    { value: "volatility", label: "Volatility" },
    { value: "returns", label: "Returns" },
    { value: "sharpe_ratio", label: "Sharpe Ratio" },
    { value: "sortino_ratio", label: "Sortino Ratio" },
    { value: "max_drawdown", label: "Max Drawdown" },
    { value: "beta", label: "Beta (vs Benchmark)" },
    { value: "correlation", label: "Correlation (vs Benchmark)" },
    { value: "underwater", label: "Underwater (Drawdown)" },
  ];

  // Create dynamic exposure type options based on fetched data
  const exposureTypeOptions: SelectOption<ExposureType>[] = useMemo(
    () =>
      availableExposureTypes.map((type) => ({
        value: type as ExposureType,
        label: type
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      })),
    [availableExposureTypes]
  );

  // Add options for scatter chart statistics
  const scatterStatisticOptions: SelectOption<string>[] = [
    { value: "generic-stat-annualized-3y", label: "3y Annualised Performance" },
    {
      value: "generic-stat-volatility-36m-m",
      label: "3y Annualised Volatility",
    },
  ];

  return (
    <div className="">
      <div className="flex flex-col space-y-4">
        {/* Playground Type Selection - Always full width */}
        <div>
          <SelectDropdown
            label="Chart Type"
            options={chartTypeOptions}
            value={playgroundType}
            onChange={setPlaygroundType}
          />
        </div>

        {/* Time Series specific controls */}
        {playgroundType === PlaygroundType.PerformanceHistory && (
          <>
            {/* Display Mode */}
            <div>
              <label className="text-xs uppercase block  tracking-widest text-slate-700 dark:text-slate-300 font-mono mb-2">
                Display Mode
              </label>
              <ButtonGroup
                options={displayModeOptions}
                value={displayMode}
                onChange={setDisplayMode}
                fullWidth={true}
              />
            </div>

            {/* Rebase Value (only for Index mode) */}
            {displayMode === "index" && (
              <div className="flex w-full">
                <FormInput
                  id="rebaseValue"
                  label="Base Value"
                  type="number"
                  value={rebaseValue}
                  onChange={(e) =>
                    setRebaseValue(parseInt(e.target.value) || 100)
                  }
                  min="1"
                />
              </div>
            )}

            <ExtendIncompleteWithProxySwitch
              checked={extendIncompleteWithProxy}
              onChange={setExtendIncompleteWithProxy}
              description="Fills window-edge gaps with dashed proxy segments. Interior gaps stay empty."
            />
          </>
        )}

        {/* Rolling Stats specific controls */}
        {playgroundType === PlaygroundType.RollingStats && (
          <>
            {/* Statistic Type */}
            <div>
              <SelectDropdown
                label="Statistic Type"
                options={statisticOptions}
                value={statistic}
                onChange={setStatistic}
              />
            </div>

            {/* Window Size */}
            <div>
              <FormInput
                id="windowSize"
                label="Window Size"
                type="number"
                value={windowSize}
                onChange={(e) => setWindowSize(parseInt(e.target.value) || 30)}
                min="1"
                max="100"
                suffix={
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    periods
                  </span>
                }
              />
            </div>
          </>
        )}

        {playgroundType === PlaygroundType.MetricsOverTime && (
          <div>
            <SelectDropdown
              label="Metric"
              options={METRIC_FIELD_PATH_OPTIONS}
              value={metricFieldPath}
              onChange={setMetricFieldPath}
            />
          </div>
        )}

        {/* Exposure specific controls */}
        {(playgroundType === PlaygroundType.Exposure ||
          playgroundType === PlaygroundType.ExposureColumns ||
          playgroundType === PlaygroundType.ExposureOverTime) && (
          <>
            {/* Exposure Type */}
            <div>
              {isLoadingExposureTypes ? (
                <div>
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Exposure Type
                  </label>
                  <div className="w-full h-8 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                </div>
              ) : exposureTypesError ? (
                <div>
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Exposure Type
                  </label>
                  <div className="w-full p-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                    {exposureTypesError}
                  </div>
                </div>
              ) : exposureTypeOptions.length > 0 ? (
                <SelectDropdown
                  label="Exposure Type"
                  options={exposureTypeOptions}
                  value={exposureType}
                  onChange={setExposureType}
                />
              ) : selectedAssets.length === 0 ? (
                <div>
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Exposure Type
                  </label>
                  <div className="w-full p-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                    Select assets to view exposure types
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Exposure Type
                  </label>
                  <div className="w-full p-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                    No exposure types available
                  </div>
                </div>
              )}
            </div>

            {playgroundType === PlaygroundType.ExposureOverTime && (
              <>
                <div>
                  <SelectDropdown
                    label="Display mode"
                    options={exposureDisplayModeOptionsFiltered}
                    value={
                      selectedResourceType === "portfolio"
                        ? exposureDisplayMode
                        : exposureDisplayMode === "values"
                          ? "scaled_weights"
                          : exposureDisplayMode
                    }
                    onChange={(value) => {
                      if (value) setExposureDisplayMode(value);
                    }}
                  />
                </div>
                <div>
                  <SelectDropdown
                    label="Frequency"
                    options={exposureOverTimeFrequencyOptions}
                    value={
                      frequency === "monthly" || frequency === "quarterly"
                        ? frequency
                        : "quarterly"
                    }
                    onChange={(value) => {
                      if (value) setFrequency(value);
                    }}
                  />
                </div>
              </>
            )}

            {playgroundType === PlaygroundType.ExposureColumns && (
              <div>
                <Button
                  color="indigo"
                  onClick={() => setIsBandsDialogOpen(true)}
                  className="w-full"
                >
                  Configure Bands
                </Button>
                {exposureBands && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Bands configured for this exposure type.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* Frequency - Performance History, Rolling Stats, and Underwater */}
        {(playgroundType === PlaygroundType.PerformanceHistory ||
          playgroundType === PlaygroundType.RollingStats ||
          playgroundType === PlaygroundType.Underwater) && (
          <>
            <div className="col-span-2">
              <SelectDropdown
                label="Frequency"
                options={frequencyOptions}
                value={frequency}
                onChange={setFrequency}
              />
            </div>
            {(playgroundType === PlaygroundType.RollingStats ||
              playgroundType === PlaygroundType.Underwater) && (
              <div className="col-span-2">
                <ExtendIncompleteWithProxySwitch
                  checked={extendIncompleteWithProxy}
                  onChange={setExtendIncompleteWithProxy}
                  description="Fills window-edge gaps with dashed proxy segments. Interior gaps stay empty."
                  className="pt-1"
                />
              </div>
            )}
          </>
        )}

        {/* Scatter Chart specific controls */}
        {playgroundType === PlaygroundType.ScatterChart && (
          <>
            {/* X Statistic */}
            <div>
              <SelectDropdown
                label="X Statistic"
                options={scatterStatisticOptions}
                value={xStatistic}
                onChange={setXStatistic}
              />
            </div>

            {/* Y Statistic */}
            <div>
              <SelectDropdown
                label="Y Statistic"
                options={scatterStatisticOptions}
                value={yStatistic}
                onChange={setYStatistic}
              />
            </div>
          </>
        )}

        {/* Statistics Table specific controls */}
        {playgroundType === PlaygroundType.StatisticsTable && (
          <div className="space-y-4">
            {/* Views Selector */}
            <div>
              <label className="text-xs uppercase block  tracking-widest text-slate-700 dark:text-slate-300 font-mono mb-0">
                Column View
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <SelectDropdown
                    options={viewOptions}
                    value={selectedViewId}
                    onChange={(val) => {
                      console.log("[DEBUG] Dropdown onChange - val:", val);
                      console.log("[DEBUG] savedViews:", savedViews);
                      if (val === null) {
                        handleSelectCustom();
                      } else {
                        const view = savedViews.find((v) => v.id === val);
                        console.log("[DEBUG] Found view:", view);
                        if (view) handleLoadView(view);
                      }
                    }}
                  />
                </div>
                {/* Save current as new view */}
                <button
                  type="button"
                  onClick={() => setShowSaveDialog(true)}
                  className="p-2 text-slate-400 hover:text-indigo-400 transition-colors"
                  title="Save current columns as view"
                >
                  <FontAwesomeIcon icon={faFloppyDisk} className="h-4 w-4" />
                </button>
              </div>
              
              {/* View actions when a saved view is selected */}
              {selectedViewId && (
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                  <span>View:</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      const view = savedViews.find((v) => v.id === selectedViewId);
                      if (view) handleSetDefaultView(view, e);
                    }}
                    className="hover:text-amber-400 transition-colors"
                    title="Set as default"
                  >
                    {savedViews.find((v) => v.id === selectedViewId)?.is_default ? (
                      <FontAwesomeIcon icon={faStarSolid} className="h-3 w-3 text-amber-400" />
                    ) : (
                      <FontAwesomeIcon icon={faStar} className="h-3 w-3" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      const view = savedViews.find((v) => v.id === selectedViewId);
                      if (view && confirm(`Delete view "${view.name}"?`)) handleDeleteView(view, e);
                    }}
                    className="hover:text-rose-400 transition-colors"
                    title="Delete view"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            {/* Column Selector - only show when in Custom mode */}
            {selectedViewId === null && (
              <div>
              
                <div className="flex flex-col mb-4">
                    <span>
                      {columns.filter(c => c.type === "statistic").length} <span className="text-slate-400 font-mono uppercase text-sm ml-2">statistic{columns.filter(c => c.type === "statistic").length !== 1 ? "s" : ""}</span>
                    </span>
                    <span>{columns.filter(c => c.type === "period_return").length} <span className="text-slate-400 font-mono uppercase text-sm ml-2">period return{columns.filter(c => c.type === "period_return").length !== 1 ? "s" : ""}</span></span>
                  </div>
                  <Button
                    color="color"
                    className="w-full"
                    onClick={() => setIsStatisticsSelectorOpen(true)}
                  >
                    Configure...
                  </Button>
              </div>
            )}
            
            <StatisticsSelector
              isOpen={isStatisticsSelectorOpen}
              onClose={() => setIsStatisticsSelectorOpen(false)}
              columns={columns}
              onColumnsChange={(newColumns) => {
                setColumns(newColumns);
                setSelectedViewId(null); // Switch to Custom when editing
              }}
            />
            
            {/* Show Benchmarks Toggle */}
            <div className="flex items-center justify-between pt-2">
              <label className="text-sm text-gray-700 dark:text-gray-300">
                Show Benchmark Rows
              </label>
              <Switch
                checked={showBenchmarks}
                onChange={setShowBenchmarks}
              >
                <span className="sr-only">Show Benchmarks</span>
              </Switch>
            </div>

            <ExtendIncompleteWithProxySwitch
              checked={extendIncompleteWithProxy}
              onChange={setExtendIncompleteWithProxy}
              description="Recalculates stats using proxy-filled window edges when product data is short."
            />

            {/* Risk-Free Rate Input */}
            <div className="flex items-center justify-between pt-2">
              <label className="text-sm text-gray-700 dark:text-gray-300">
                Risk-Free Rate
              </label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={(riskFreeRate * 100).toFixed(2)}
                  onChange={(e) => {
                    const pct = parseFloat(e.target.value);
                    if (!isNaN(pct)) {
                      setRiskFreeRate(pct / 100);
                    }
                  }}
                  step="0.25"
                  min="0"
                  max="20"
                  className="w-16 px-2 py-1 text-sm text-right rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
              </div>
            </div>
            
            {/* Save View Dialog */}
            <Dialog open={showSaveDialog} onClose={() => setShowSaveDialog(false)} size="sm">
              <DialogTitle>Save Column View</DialogTitle>
              <DialogBody>
                <div className="space-y-4">
                  <FormInput
                    label="View Name"
                    value={saveViewName}
                    onChange={(e) => setSaveViewName(e.target.value)}
                    placeholder="e.g., Performance Summary"
                    required
                  />
                  <FormInput
                    label="Description (optional)"
                    value={saveViewDescription}
                    onChange={(e) => setSaveViewDescription(e.target.value)}
                    placeholder="e.g., Key performance metrics"
                  />
                </div>
              </DialogBody>
              <DialogActions>
                <Button
                  color="color"
                  onClick={() => setShowSaveDialog(false)}
                  disabled={savingView}
                >
                  Cancel
                </Button>
                <Button
                  color="indigo"
                  onClick={handleSaveView}
                  disabled={!saveViewName.trim() || savingView}
                >
                  {savingView ? "Saving..." : "Save View"}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        )}
      </div>

      {playgroundType === PlaygroundType.ExposureColumns && onSaveExposureBands && (
        <ExposureBandsDialog
          isOpen={isBandsDialogOpen}
          onClose={() => setIsBandsDialogOpen(false)}
          categories={exposureCategories}
          savedBands={exposureBands}
          onSave={onSaveExposureBands}
          onClear={onClearExposureBands || (() => {})}
        />
      )}
    </div>
  );
};

export default WidgetConfig;
